import { useState, useEffect, useCallback } from 'react';

export type ViewMode =
  | 'home'
  | 'order_rolls'
  | 'hunting'
  | 'album'
  | 'inventory'
  | 'leaderboard'
  | 'achievements'
  | 'privacy'
  | 'terms';

export const ROUTE_MAP: Record<string, ViewMode> = {
  '/': 'home',
  '/home': 'home',
  '/order-rolls': 'order_rolls',
  '/order': 'order_rolls',
  '/bank': 'order_rolls',
  '/hunting': 'hunting',
  '/hunt': 'hunting',
  '/album': 'album',
  '/collection': 'album',
  '/inventory': 'inventory',
  '/sales': 'inventory',
  '/market': 'inventory',
  '/leaderboard': 'leaderboard',
  '/leaderboards': 'leaderboard',
  '/achievements': 'achievements',
  '/achievement': 'achievements',
  '/privacy': 'privacy',
  '/privacy-policy': 'privacy',
  '/terms': 'terms',
  '/terms-of-service': 'terms',
};

export const VIEW_TO_PATH: Record<ViewMode, string> = {
  home: '/',
  order_rolls: '/order-rolls',
  hunting: '/hunting',
  album: '/album',
  inventory: '/inventory',
  leaderboard: '/leaderboard',
  achievements: '/achievements',
  privacy: '/privacy',
  terms: '/terms',
};

export function getPathFromView(view: ViewMode): string {
  return VIEW_TO_PATH[view] || '/';
}

export function getViewFromPath(pathname: string): ViewMode {
  const normalized = pathname.toLowerCase().replace(/\/+$/, '') || '/';
  return ROUTE_MAP[normalized] || 'home';
}

export function navigateTo(viewOrPath: ViewMode | string, options: { replace?: boolean } = {}) {
  let targetPath: string;
  if (viewOrPath in VIEW_TO_PATH) {
    targetPath = VIEW_TO_PATH[viewOrPath as ViewMode];
  } else {
    targetPath = viewOrPath.startsWith('/') ? viewOrPath : `/${viewOrPath}`;
  }

  if (window.location.pathname !== targetPath) {
    if (options.replace) {
      window.history.replaceState({}, '', targetPath);
    } else {
      window.history.pushState({}, '', targetPath);
    }
    window.dispatchEvent(new PopStateEvent('popstate'));
  }
}

export function useRouter() {
  const [currentPath, setCurrentPath] = useState<string>(() => window.location.pathname);
  const [currentView, setCurrentView] = useState<ViewMode>(() =>
    getViewFromPath(window.location.pathname)
  );

  useEffect(() => {
    const handleLocationChange = () => {
      const path = window.location.pathname;
      setCurrentPath(path);
      setCurrentView(getViewFromPath(path));
    };

    window.addEventListener('popstate', handleLocationChange);
    return () => {
      window.removeEventListener('popstate', handleLocationChange);
    };
  }, []);

  const navigate = useCallback((target: ViewMode | string, options?: { replace?: boolean }) => {
    navigateTo(target, options);
  }, []);

  return {
    currentPath,
    currentView,
    navigate,
    setCurrentView: (view: ViewMode) => navigateTo(view),
  };
}
